import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  advanceFor, balanceDue, calc, customerStats, dateOf, dur, fDate, fDT, fRange, fShort, fTime, inr, itemUnits, renderMessage,
  span, suggestTemplates, toInstant, type BookingRecord, type DocCategory, type Phase,
} from '@nandanam/domain';
import { useStore, type AppStore } from '../app/store';
import { Icon } from '../ui/icons';
import { StatusPill } from '../ui/components';
import { BAR, DEP_LABEL, PAYTYPE, PKGIMG, kindLabel, pkgLabel } from '../app/selectors';
import { fmtSize, MAX_UPLOAD_BYTES, openDoc } from '../app/files';

export type Act = { label: string; go: () => void; danger?: boolean };

/** Context actions per phase (handoff table). Shared by desktop and mobile. */
export function recordActions(S: AppStore, r: BookingRecord): { primary: Act | null; secondary: Act[]; more: Act[] } {
  const ph = S.phase(r), k = calc(r), id = r.id;
  const O = (name: string, extra: Record<string, unknown> = {}) => () => S.open(name, { id, ...extra });
  const bill = () => S.nav('bill', { id });
  const extend = () => S.act('record.extendHold', `record:${id}`, { id, hours: S.state.settings.holdExtendHours }, `Hold extended by ${S.state.settings.holdExtendHours} h`).then(x => { if (!x.ok) S.toast(x.error!); });
  const reopenIt = () => S.act('record.reopen', `record:${id}`, { id }, 'Reopened as an enquiry').then(x => { if (!x.ok) S.toast(x.error!); });
  const newFor = () => S.open('new', { cust: r.cust });
  if (ph === 'enquiry' && k.pending) return { primary: { label: 'Set agreed price', go: O('pricing') }, secondary: [{ label: 'Send quote', go: O('message', { tpl: 'quotation' }) }, { label: 'Confirm booking', go: O('confirm') }], more: [{ label: 'Edit details', go: O('edit') }, { label: 'Mark as lost', go: O('lost'), danger: true }] };
  switch (ph) {
    case 'enquiry': return { primary: { label: 'Place hold', go: O('hold') }, secondary: [{ label: 'Send quote', go: O('message', { tpl: 'quotation' }) }, { label: 'Confirm booking', go: O('confirm') }], more: [{ label: 'Edit details', go: O('edit') }, { label: 'Adjust pricing', go: O('pricing') }, { label: 'Mark as lost', go: O('lost'), danger: true }] };
    case 'hold': return { primary: { label: 'Confirm booking', go: O('confirm') }, secondary: [{ label: 'Send hold message', go: O('message', { tpl: 'hold' }) }, { label: `Extend ${S.state.settings.holdExtendHours}h`, go: extend }], more: [{ label: 'Edit details', go: O('edit') }, { label: 'Adjust pricing', go: O('pricing') }, { label: 'Release hold', go: O('lost', { release: true }), danger: true }] };
    case 'expired': return { primary: { label: 'Hold again', go: O('hold') }, secondary: [{ label: 'Send quote', go: O('message', { tpl: 'quotation' }) }], more: [{ label: 'Edit details', go: O('edit') }, { label: 'Mark as lost', go: O('lost'), danger: true }] };
    case 'upcoming': return { primary: k.balance > 0 ? { label: 'Record payment', go: O('payment') } : { label: 'Send instructions', go: O('message', { tpl: r.kind === 'stay' ? 'prearrival' : 'event' }) }, secondary: [{ label: 'WhatsApp', go: O('message') }], more: [{ label: 'Edit details', go: O('edit') }, { label: 'Adjust pricing', go: O('pricing') }, { label: 'Collect deposit', go: O('deposit') }, { label: 'Print pro-forma bill', go: bill }, ...(k.balance < 0 ? [{ label: 'Refund overpayment', go: O('refund') }] : []), { label: 'Cancel booking', go: O('cancel'), danger: true }] };
    case 'inprogress': return { primary: { label: 'Start check-out', go: O('checkout') }, secondary: [{ label: 'Record payment', go: O('payment') }, ...(k.dStatus === 'due' ? [{ label: 'Collect deposit', go: O('deposit') }] : [])], more: [{ label: 'WhatsApp', go: O('message') }, { label: 'Print bill', go: bill }, { label: 'Adjust pricing', go: O('pricing') }] };
    case 'closing': return { primary: { label: 'Complete and settle deposit', go: O('checkout') }, secondary: [{ label: 'Record payment', go: O('payment') }], more: [{ label: 'Print bill', go: bill }, { label: 'WhatsApp', go: O('message') }, ...(k.balance < 0 ? [{ label: 'Refund overpayment', go: O('refund') }] : []), { label: 'Adjust pricing', go: O('pricing') }] };
    case 'completed': return { primary: { label: 'Print bill', go: bill }, secondary: [{ label: 'Send thank-you', go: O('message', { tpl: 'thanks' }) }], more: [{ label: 'New booking for this customer', go: newFor }] };
    case 'cancelled': return { primary: { label: 'Print cancellation note', go: bill }, secondary: [{ label: 'Send cancellation message', go: O('message', { tpl: 'cancel' }) }], more: [{ label: 'New enquiry for this customer', go: newFor }] };
    case 'lost': return { primary: { label: 'Reopen as enquiry', go: reopenIt }, secondary: [], more: [{ label: 'New enquiry for this customer', go: newFor }] };
  }
}

export function trackFor(S: AppStore, r: BookingRecord) {
  const ph = S.phase(r);
  const pos = ({ enquiry: 0, hold: 1, expired: 1, upcoming: 2, inprogress: 3, closing: 3, completed: 4, cancelled: 2, lost: 0 } as Record<Phase, number>)[ph];
  const branch = ['expired', 'cancelled', 'lost'].includes(ph);
  const steps = ['Enquiry', 'Hold', 'Confirmed', r.kind === 'stay' ? 'Stay' : 'Event', 'Completed'].map((label, i) => ({ label, done: i < pos || (i === pos && ph === 'completed'), cur: i === pos && !branch && ph !== 'completed' }));
  const note = ph === 'expired' && r.hold ? `Hold expired ${fDT(r.hold.expires)} — dates released`
    : ph === 'cancelled' && r.cancel ? `Cancelled ${fDate(r.cancel.date)} — dates released`
    : ph === 'lost' && r.lost ? `Lost ${fDate(r.lost.date)} — ${r.lost.reason}`
    : ph === 'hold' && r.hold ? `Hold expires ${fDT(r.hold.expires)} · ${dur(toInstant(r.hold.expires) - S.now)} left`
    : ph === 'closing' ? (r.checkout ? 'Checked out — settle balance and deposit to close' : 'Ended — check out, settle balance and deposit to close') : '';
  return { steps, note };
}

export function capacityInfo(S: AppStore, r: BookingRecord) {
  const pk = r.items.map(i => S.pkg(i.pkg));
  const cap = Math.max(...pk.map(p => p?.capacity ?? 0)), mx = Math.max(...pk.map(p => p?.max ?? p?.capacity ?? 0));
  const warn = r.guests > mx ? `${r.guests} guests is over the maximum of ${mx}. Confirm with the customer or move to a bigger package.`
    : r.guests > cap ? `${r.guests - cap} guest${r.guests - cap > 1 ? 's' : ''} over the standard capacity of ${cap}. ${r.extra ? 'Extra-guest charge applied: ' + inr(r.extra.amount) + '.' : 'No extra charge applied — your call.'}` : '';
  return { cap, mx, warn };
}

export function chargeRows(r: BookingRecord) {
  const k = calc(r);
  const rows: { k: string; n: string; std: string; v: string }[] = r.items.map(i => {
    if (i.custom) return { k: i.pkgName, n: i.agreed ? 'Price agreed after discussion' : 'Price to be agreed after discussion', std: '—', v: i.agreed ? inr(i.agreed) : 'To be agreed' };
    const d = i.agreed - i.std; return { k: i.pkgName, n: d ? `${d > 0 ? '+' : '−'}${inr(Math.abs(d))} vs standard` : '', std: inr(i.std), v: inr(i.agreed) };
  });
  if (k.extra) rows.push({ k: 'Extra guests', n: r.extra?.note || `${r.extra?.guests} over capacity`, std: '', v: inr(k.extra) });
  if (k.clean) rows.push({ k: 'Cleaning', n: '', std: inr(k.clean), v: inr(k.clean) });
  if (k.disc) rows.push({ k: 'Discount', n: r.discount?.reason ?? '', std: '', v: '−' + inr(k.disc) });
  if (k.damage) rows.push({ k: 'Damage beyond deposit', n: 'Billed separately', std: '', v: inr(k.damage) });
  // Customize items have no standard, so they are left out of the comparison.
  const stdTotal = k.std + k.clean, diff = k.total - k.damage - stdTotal - k.custom;
  return { rows, stdTotal, diff, note: diff && stdTotal ? `${diff > 0 ? 'Above' : 'Below'} standard by ${inr(Math.abs(diff))} (${Math.round((Math.abs(diff) / stdTotal) * 100)}%). Standard rate is kept for pricing history.` : '' };
}

export function ledgerRows(r: BookingRecord) {
  const k = calc(r);
  let run = k.total - k.damage;
  const out = [{ id: 'charges', date: fDate(r.created, { dow: false }), t: 'Booking charges', mode: '', ref: pkgLabel(r), amt: inr(run), bal: inr(run), voided: false, by: '' }];
  if (k.damage) { run += k.damage; out.push({ id: 'damage', date: '', t: 'Damage beyond deposit', mode: '', ref: '', amt: inr(k.damage), bal: inr(run), voided: false, by: '' }); }
  for (const p of r.payments) {
    if (!p.voided) run += p.type === 'refund' ? p.amount : -p.amount;
    out.push({ id: p.id, date: fDate(p.date, { dow: false }), t: PAYTYPE[p.type]! + (p.voided ? ` — voided: ${p.voided.reason}` : ''), mode: p.mode, ref: p.ref || '—', amt: (p.type === 'refund' ? '+' : '−') + inr(p.amount), bal: p.voided ? '' : inr(run), voided: !!p.voided, by: p.by });
  }
  return out;
}

export function activityFor(S: AppStore, r: BookingRecord) {
  const comms = r.comms.map(c => ({ d: c.date, t: `${c.action === 'picture' ? 'Quotation picture shared' : c.action === 'copied' ? 'WhatsApp message copied' : 'WhatsApp opened with message'} — ${S.state.settings.templates[c.key]?.label ?? c.key} (not proof it was sent)`, by: c.by }));
  return [...r.log, ...comms].sort((a, b) => (a.d < b.d ? 1 : a.d > b.d ? -1 : 0));
}

const TABS = [['overview', 'Overview'], ['payments', 'Payments'], ['deposit', 'Deposit'], ['documents', 'Documents'], ['comms', 'Communication'], ['activity', 'Activity']] as const;
export const DOC_CATS: DocCategory[] = ['ID proof', 'Payment screenshot', 'Agreement', 'Damage evidence', 'Refund evidence', 'Other'];

export function Record() {
  const S = useStore();
  const r = S.rec(S.route.params.id ?? '');
  const [more, setMore] = useState(false);
  if (!r) return <div className="page"><div className="card empty"><b>Record not found</b>It may have been created on another device that has not synced yet.<div style={{ marginTop: 12 }}><button className="btn" onClick={() => S.nav('bookings')}>Back to bookings</button></div></div></div>;
  const tab = S.route.params.tab ?? 'overview';
  const setTab = (t: string) => S.nav('booking', { id: r.id, tab: t });
  const ph = S.phase(r), c = S.cust(r.cust), k = calc(r), sp = span(r);
  const isEnqSide = ['enquiry', 'hold', 'expired', 'lost'].includes(ph);
  const { primary, secondary, more: moreActs } = recordActions(S, r);
  const track = trackFor(S, r), cap = capacityInfo(S, r);
  const bar = BAR[r.kind === 'stay' ? 'stay' : 'event'];
  const issues = recordIssues(S, r);
  const editable = S.canEdit && ['enquiry', 'hold', 'expired', 'upcoming'].includes(ph);
  const facts: { k: string; v: string; n: string; warn?: boolean; edit?: boolean }[] = [
    { k: 'When', edit: editable, v: fShort(sp.start, sp.end), n: dateOf(sp.start) === dateOf(sp.end) ? `${fTime(sp.start)} – ${fTime(sp.end)}` : `${fTime(sp.start)} → ${fTime(sp.end)}` },
    { k: 'Package', edit: editable, v: r.items.length > 1 ? 'Event + stay' : r.items[0]!.pkgName, n: r.type },
    { k: 'Guests', edit: editable, v: String(r.guests), n: r.items.every(i => i.custom) ? 'Small event' : `Capacity ${cap.cap}${cap.mx > cap.cap ? ' (max ' + cap.mx + ')' : ''}`, warn: !r.items.every(i => i.custom) && r.guests > cap.cap },
    { k: 'Total', v: k.pending ? 'To be agreed' : inr(k.total), n: k.pending ? 'Agree the price, then adjust pricing' : k.custom ? 'Small-event price agreed' : k.std !== k.agreed ? `Standard ${inr(k.std)}` : 'At standard rate' },
    { k: 'Balance', v: isEnqSide || ph === 'cancelled' ? '—' : inr(k.balance), n: ph === 'cancelled' ? 'Cancelled' : isEnqSide ? 'Not yet booked' : `${k.payState} · ${inr(k.paid)} received`, warn: k.balance > 0 && !isEnqSide && ph !== 'cancelled' },
    { k: 'Deposit', v: isEnqSide || ph === 'cancelled' ? inr(k.dRequired) : DEP_LABEL[k.dStatus]!, n: isEnqSide ? 'Collected on arrival' : k.dHeld ? `${inr(k.dHeld)} held` : k.dStatus === 'due' ? `${inr(k.dRequired)} to collect` : `${inr(k.dRefunded)} refunded` },
  ];
  const counts: Record<string, number> = { payments: r.payments.length, documents: r.docs.length, comms: r.comms.length };
  const ro = !S.canEdit;
  return (
    <div className="page">
      <div>
        <button className="crumb" onClick={() => S.nav(isEnqSide ? 'enquiries' : 'bookings')}><Icon name="left" size={15} />{isEnqSide ? 'Enquiries' : 'Bookings'}</button>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 6 }}>
          <h1 className="page-title" style={{ cursor: 'pointer' }} onClick={() => S.nav('customer', { id: r.cust })}>{c.name}</h1>
          <StatusPill phase={ph} />
          <span className="pill" style={{ background: bar.bg, color: bar.fg, border: `1px solid ${bar.bc}` }}><Icon name={bar.icon} size={12} />{kindLabel(r)}</span>
          <span className="pill st-neutral num" title={S.isProvisional(r) ? 'Number becomes final a few minutes after it reaches OneDrive' : ''}>{S.num(r)}{S.isProvisional(r) ? ' · provisional' : ''}</span>
          {S.isPendingRecord(r) && <span className="pill st-warn">Not in OneDrive yet</span>}
          <div className="head-actions" style={{ position: 'relative' }}>
            {secondary.map(a => <button key={a.label} className="btn" disabled={ro} onClick={a.go}>{a.label}</button>)}
            {primary && <button className="btn primary" disabled={ro && !/Print/.test(primary.label)} onClick={primary.go}>{primary.label}</button>}
            {moreActs.length > 0 && <button className="icon-btn" aria-label="More actions" onClick={() => setMore(!more)}><Icon name="more" size={16} /></button>}
            {more && (
              <div className="menu" onMouseLeave={() => setMore(false)}>
                {moreActs.map(a => <button key={a.label} disabled={ro && !/Print/.test(a.label)} style={{ color: a.danger ? 'var(--bad-text)' : undefined }} onClick={() => { setMore(false); a.go(); }}>{a.label}</button>)}
              </div>
            )}
          </div>
        </div>
        <div className="page-sub">{kindLabel(r)} · {r.type} · enquiry via {r.source} on {fDate(r.created)}</div>
      </div>

      {issues.map((x, i) => <div key={i} className="warn-box" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><Icon name="alert" size={16} /><div>{x}</div></div>)}

      <div className="card tight">
        <div className="track">
          {track.steps.map((s, i) => (
            <div key={s.label} className={`track-step ${s.done ? 'done' : ''} ${s.cur ? 'cur' : ''}`}>
              <span className="dot">{s.done ? '✓' : s.cur ? '●' : ''}</span><span className="lbl">{s.label}</span>{i < 4 && <span className="line" />}
            </div>
          ))}
        </div>
        {track.note && <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>{track.note}</div>}
      </div>

      <div className="facts">
        {facts.map(f => {
          const inner = <><div className="k">{f.k}{f.edit && <span className="fact-edit"><Icon name="edit" size={12} />Edit</span>}</div><div className="v num" style={{ color: f.warn ? 'var(--warn)' : undefined }}>{f.v}</div><div className="n">{f.n}</div></>;
          return f.edit ? <button key={f.k} type="button" className="fact editable" onClick={() => S.open('edit', { id: r.id })}>{inner}</button> : <div key={f.k} className="fact">{inner}</div>;
        })}
      </div>

      <div className="tabs" role="tablist">
        {TABS.map(([key, label]) => <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>{label}{counts[key] ? <span className="c">{counts[key]}</span> : null}</button>)}
      </div>

      {tab === 'overview' && <OverviewTab r={r} />}
      {tab === 'payments' && <PaymentsTab r={r} />}
      {tab === 'deposit' && <DepositTab r={r} />}
      {tab === 'documents' && <DocumentsTab r={r} />}
      {tab === 'comms' && <CommsTab r={r} />}
      {tab === 'activity' && <ActivityTab r={r} />}
    </div>
  );
}

export function recordIssues(S: AppStore, r: BookingRecord): string[] {
  const out: string[] = [];
  for (const c of S.view.clashes) if (c.a === r.id || c.b === r.id) {
    const other = c.a === r.id ? c.b : c.a, o = S.rec(other);
    out.push(`Clashing reservation: these dates overlap ${o ? `${S.cust(o.cust).name} (${S.num(o)})` : 'an owner block'}. Both were saved on different devices before either saw the other. Decide which one stays.`);
  }
  for (const x of S.view.reviews) if (x.recordId === r.id) out.push(x.reason);
  for (const x of S.view.rejected) if (x.op.args?.id === r.id) out.push(`A change made by ${x.op.actor} on ${fDT(x.op.at)} could not be applied: ${x.reason}. Nothing was lost — see Account & sync → Review.`);
  for (const x of S.view.edits) if (x.entity === `record:${r.id}`) out.push('This record was edited on two devices at the same time. The later change is shown; both are in the Activity tab.');
  return out;
}

function Box({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return <section className="card"><div className="section-head"><h3 className="card-sub">{title}</h3>{action}</div>{children}</section>;
}

function OverviewTab({ r }: { r: BookingRecord }) {
  const S = useStore(), ph = S.phase(r), k = calc(r), cap = capacityInfo(S, r), ch = chargeRows(r);
  const cs = customerStats(S.state.records, r.cust, S.now), c = S.cust(r.cust);
  const [notes, setNotes] = useState(r.notes);
  useEffect(() => setNotes(r.notes), [r.notes]);
  const saveNotes = async () => { if (notes === r.notes) return; const x = await S.draft('record.notes', `record:${r.id}`, { id: r.id, notes }, 'Notes saved'); if (!x.ok) S.toast(x.error!); };
  const ro = !S.canEdit;
  const canPrice = !['completed', 'cancelled', 'lost'].includes(ph);
  return (
    <div className="two-col">
      <div className="col">
        <Box title="Requirement">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {r.items.map((i, n) => {
              const p = S.pkg(i.pkg), u = itemUnits(i);
              return (
                <div key={n} className="item-row">
                  <img src={PKGIMG[i.pkg] ?? 'assets/house-path.jpg'} alt="" />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{i.pkgName} <span className="muted" style={{ fontWeight: 400 }}>· {i.kind === 'stay' ? `${u} × 24 hours` : `${u} day${u > 1 ? 's' : ''}`}</span></div>
                    <div style={{ fontSize: 13 }}>{fRange(i.start, i.end)}</div>
                    <div className="muted" style={{ fontSize: 12.5 }}>{(p?.areas ?? []).join(', ')}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="kv" style={{ marginTop: 12 }}>
            <div><span>Guests</span><span>{r.guests} guests</span></div>
            <div><span>Source</span><span>{r.source}</span></div>
            {['enquiry', 'hold'].includes(ph) && <div><span>Follow up</span><span><input type="date" className="input" style={{ minHeight: 34, padding: '4px 8px', width: 170 }} disabled={ro} value={r.followUp ?? ''} onChange={e => S.act('record.followUp', `record:${r.id}`, { id: r.id, date: e.target.value || null }, 'Follow-up saved').then(x => { if (!x.ok) S.toast(x.error!); })} /></span></div>}
          </div>
          {cap.warn && <div className="warn-box" style={{ marginTop: 12 }}>{cap.warn}</div>}
        </Box>
        <Box title="Notes">
          <textarea className="textarea" value={notes} disabled={ro} onChange={e => setNotes(e.target.value)} onBlur={saveNotes} placeholder="Anything to remember — caterer, decorators, special requests…" />
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Saves when you click away.</div>
        </Box>
        {r.cancel && (
          <Box title="Cancellation">
            <div className="kv">
              <div><span>Cancelled on</span><span>{fDate(r.cancel.date)}</span></div><div><span>Reason</span><span>{r.cancel.reason}</span></div>
              <div><span>Amount paid</span><span>{inr(r.cancel.paid)}</span></div><div><span>Refunded</span><span>{inr(r.cancel.refund)}</span></div>
              <div><span>Retained</span><span>{inr(r.cancel.retained)}</span></div><div><span>Notes</span><span>{r.cancel.notes || '—'}</span></div>
            </div>
          </Box>
        )}
      </div>
      <div className="col">
        <Box title="Charges" action={canPrice && <button className="btn small" disabled={ro} onClick={() => S.open('pricing', { id: r.id })}>{k.pending ? 'Set agreed price' : 'Adjust pricing'}</button>}>
          <table className="t compact">
            <thead><tr><th>Item</th><th className="r">Standard</th><th className="r">Agreed</th></tr></thead>
            <tbody>
              {ch.rows.map((x, i) => <tr key={i}><td><div style={{ fontWeight: 600 }}>{x.k}</div>{x.n && <div className="muted" style={{ fontSize: 12 }}>{x.n}</div>}</td><td className="r num muted">{x.std}</td><td className="r num">{x.v}</td></tr>)}
              <tr><td style={{ fontWeight: 700 }}>Total</td><td className="r num muted">{inr(ch.stdTotal)}</td><td className="r num" style={{ fontWeight: 700, fontSize: 16 }}>{k.pending ? 'To be agreed' : inr(k.total)}</td></tr>
            </tbody>
          </table>
          {ch.note && <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>{ch.note}</div>}
        </Box>
        <Box title="Terms">
          <div className="kv">
            <div><span>Advance ({r.terms.advanceMode === 'percent' ? r.terms.advancePct + '%' : 'flat'})</span><span>{inr(advanceFor(k.total, r.terms))}</span></div>
            <div><span>Balance due by</span><span>{fDate(balanceDue(r))}</span></div>
            <div><span>Caution deposit</span><span>{inr(k.dRequired)} · refundable</span></div>
            <div><span>Received so far</span><span>{inr(k.paid)}</span></div>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>Terms are saved with the booking; later changes in Settings do not alter them.</div>
        </Box>
        <Box title="Customer" action={<button className="btn small" onClick={() => S.nav('customer', { id: r.cust })}>Open</button>}>
          <div style={{ fontWeight: 700 }}>{c.name}</div>
          <div className="num" style={{ fontSize: 13 }}>{c.phone}{c.whatsapp !== c.phone ? ` · WhatsApp ${c.whatsapp}` : ''}</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{cs.bookings} booking{cs.bookings === 1 ? '' : 's'} · lifetime {inr(cs.value)}{cs.outstanding ? ` · ${inr(cs.outstanding)} outstanding` : ''}</div>
        </Box>
      </div>
    </div>
  );
}

function PaymentsTab({ r }: { r: BookingRecord }) {
  const S = useStore(), ph = S.phase(r), k = calc(r), ro = !S.canEdit;
  const canPay = ['upcoming', 'inprogress', 'closing'].includes(ph);
  const d = r.deposit;
  return (
    <div className="col">
      <section className="card">
        <div className="section-head">
          <div className="kpis" style={{ flex: 1, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
            {[['Total charges', inr(k.total)], ['Received', inr(k.received)], ...(k.refunded ? [['Refunded', inr(k.refunded)]] : []), [ph === 'cancelled' ? 'Retained' : 'Balance', ph === 'cancelled' ? inr(k.paid) : inr(k.balance)]].map(([a, b]) => <div key={a} className="mini-fig"><div className="muted">{a}</div><b className="num">{b}</b></div>)}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => S.nav('bill', { id: r.id })}><Icon name="printer" size={15} />Print bill</button>
            {canPay && <button className="btn primary" disabled={ro} onClick={() => S.open('payment', { id: r.id })}><Icon name="plus" size={15} />Record payment</button>}
          </div>
        </div>
        <div className="table-wrap">
          <table className="t">
            <thead><tr><th>Date</th><th>Entry</th><th>Mode</th><th>Reference</th><th className="r">Amount</th><th className="r">Balance</th><th /></tr></thead>
            <tbody>
              {ledgerRows(r).map(x => (
                <tr key={x.id} style={{ opacity: x.voided ? 0.55 : 1, textDecoration: x.voided ? 'line-through' : 'none' }}>
                  <td className="num">{x.date}</td><td>{x.t}{x.by && <div className="muted" style={{ fontSize: 11.5 }}>by {x.by}</div>}</td><td>{x.mode}</td><td className="muted">{x.ref}</td><td className="r num">{x.amt}</td><td className="r num">{x.bal}</td>
                  <td className="r">{!x.voided && !['charges', 'damage'].includes(x.id) && canPay && <button className="btn small ghost" disabled={ro} onClick={() => S.open('void', { id: r.id, paymentId: x.id })}>Void</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="card dashed">
        <div className="section-head"><h3 className="card-sub">Caution deposit</h3><span className="muted" style={{ fontSize: 12.5 }}>Held separately — not booking revenue</span></div>
        <div className="kv">
          <div><span>Required for {pkgLabel(r)}</span><span>{inr(k.dRequired)}</span></div>
          {d.collected && <div><span>Collected {fDate(d.collected.date, { dow: false })} · {d.collected.mode}</span><span>{inr(d.collected.amount)}</span></div>}
          {d.deductions.map(x => <div key={x.id}><span>Deduction — {x.reason}</span><span>−{inr(x.amount)}</span></div>)}
          {d.refund && <div><span>Refunded {fDate(d.refund.date, { dow: false })} · {d.refund.mode}</span><span>−{inr(d.refund.amount)}</span></div>}
        </div>
      </section>
    </div>
  );
}

function DepositTab({ r }: { r: BookingRecord }) {
  const S = useStore(), ph = S.phase(r), k = calc(r), d = r.deposit, ro = !S.canEdit;
  const isEnqSide = ['enquiry', 'hold', 'expired', 'lost'].includes(ph);
  const idx = ({ due: 0, held: 1, inspection: 2, refunded: 3, partial: 3, forfeited: 3 } as Record<string, number>)[k.dStatus]!;
  const steps = ['Not collected', 'Held', 'Inspection', k.dStatus === 'partial' ? 'Partly refunded' : k.dStatus === 'forfeited' ? 'Forfeited' : 'Refunded'];
  let next: { title: string; body: string; label?: string; go?: () => void } = { title: 'Nothing to do', body: 'The deposit is settled.' };
  if (isEnqSide) next = { title: 'Collected on arrival', body: `${inr(k.dRequired)} will be due at check-in once the booking is confirmed.` };
  else if (ph === 'cancelled') next = { title: 'Not applicable', body: 'Booking cancelled before arrival.' };
  else if (k.dStatus === 'due') next = { title: 'Collect at arrival', body: `${inr(k.dRequired)} caution deposit, refundable after inspection.`, label: 'Record deposit collected', go: () => S.open('deposit', { id: r.id }) };
  else if (['held', 'inspection'].includes(k.dStatus) && ['inprogress', 'closing'].includes(ph)) next = { title: 'Inspect and settle', body: 'Walk the property with the guest, note any damage with photos, then refund the rest.', label: 'Start inspection', go: () => S.open('checkout', { id: r.id }) };
  else if (k.dStatus === 'held') next = { title: 'Held until check-out', body: `${inr(k.dHeld)} is held. Inspect after the ${r.kind === 'stay' ? 'stay' : 'event'}.` };
  return (
    <div className="two-col">
      <div className="col">
        <section className="card">
          <div className="steps4">{steps.map((s, i) => <div key={s} className={i <= idx ? 'on' : ''}><span className="num">{String(i + 1).padStart(2, '0')}</span>{s}</div>)}</div>
          <div className="kpis" style={{ marginTop: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))' }}>
            {[['Required', k.dRequired], ['Collected', k.dCollected], ['Deducted', k.dDeducted], ['Refunded', k.dRefunded], ['Held now', k.dHeld]].map(([a, b]) => <div key={a as string} className="mini-fig"><div className="muted">{a}</div><b className="num">{inr(b as number)}</b></div>)}
          </div>
        </section>
        <Box title="Inspection">
          {d.deductions.length ? <div className="kv">{d.deductions.map(x => <div key={x.id}><span>{x.reason}</span><span>−{inr(x.amount)}</span></div>)}</div> : <div className="muted" style={{ fontSize: 13 }}>{k.dStatus === 'refunded' ? 'No damage found — refunded in full.' : 'No inspection recorded yet.'}</div>}
          {d.notes && <div style={{ fontSize: 13, marginTop: 10 }}>{d.notes}</div>}
          {k.damage > 0 && <div className="warn-box" style={{ marginTop: 10 }}>Deductions exceeded the deposit; {inr(k.damage)} was added to the booking as “Damage beyond deposit”.</div>}
          {r.docs.some(x => x.cat === 'Damage evidence') && <DocGrid docs={r.docs.filter(x => x.cat === 'Damage evidence')} />}
        </Box>
      </div>
      <div className="col">
        <section className="card" style={{ background: '#f5f9f8' }}>
          <div className="kicker-sm">Next step</div>
          <h3 className="card-sub" style={{ marginTop: 4 }}>{next.title}</h3>
          <p style={{ fontSize: 13.5, margin: '6px 0 12px' }}>{next.body}</p>
          {next.label && <button className="btn primary" disabled={ro} onClick={next.go}>{next.label}</button>}
          <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>Refunds are made within {r.terms.refundWindow} by UPI or bank transfer after inspection.</div>
        </section>
      </div>
    </div>
  );
}

export function DocGrid({ docs }: { docs: BookingRecord['docs'] }) {
  const S = useStore();
  const open = (d: BookingRecord['docs'][number]) => openDoc(S, d, S.mode === 'demo' ? 'Sample file — no content in demo data' : 'This file is not in the OneDrive folder');
  return (
    <div className="doc-grid">
      {docs.map(d => (
        <button key={d.id} className="doc" onClick={() => open(d)}>
          <Icon name={/^image\//.test(d.mime) ? 'image' : 'file'} size={22} />
          <div style={{ minWidth: 0, textAlign: 'left' }}><div className="ellipsis" style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</div><div className="muted" style={{ fontSize: 11.5 }}>{fmtSize(d.size)} · {fDate(d.date, { dow: false })}{!d.driveId && S.mode !== 'demo' ? ' · not in OneDrive yet' : ''}</div></div>
        </button>
      ))}
    </div>
  );
}

export function useUpload(r: BookingRecord) {
  const S = useStore();
  const input = useRef<HTMLInputElement>(null);
  const [cat, setCat] = useState<DocCategory>('ID proof');
  const [busy, setBusy] = useState(false);
  const onFile = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) { S.toast('That file is over 20 MB'); return; }
    setBusy(true);
    try {
      const id = S.newId();
      const doc = await S.files.put(f, { id, folder: `documents/${r.id}`, cat, date: dateOf(S.now) });
      const x = await S.act('record.addDoc', `record:${r.id}`, { id: r.id, doc }, 'Document added');
      if (!x.ok) S.toast(x.error!);
    } catch (e) { S.toast(`Upload failed: ${e instanceof Error ? e.message : e}`); }
    finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  const picker = <input ref={input} type="file" accept="image/*,application/pdf" capture={undefined} style={{ display: 'none' }} onChange={e => onFile(e.target.files?.[0])} />;
  return { cat, setCat, busy, pick: () => input.current?.click(), picker };
}

function DocumentsTab({ r }: { r: BookingRecord }) {
  const S = useStore(), up = useUpload(r);
  const groups = DOC_CATS.map(cat => ({ cat, docs: r.docs.filter(d => d.cat === cat) })).filter(g => g.docs.length);
  return (
    <section className="card">
      <div className="section-head">
        <h3 className="card-sub">Booking documents</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="select" style={{ width: 200, minHeight: 38 }} value={up.cat} onChange={e => up.setCat(e.target.value as DocCategory)}>{DOC_CATS.map(c => <option key={c}>{c}</option>)}</select>
          <button className="btn primary" disabled={!S.canEdit || up.busy} onClick={up.pick}><Icon name="upload" size={15} />{up.busy ? 'Uploading…' : 'Add file'}</button>
          {up.picker}
        </div>
      </div>
      {groups.length === 0 ? <div className="empty"><b>No documents yet</b>ID proofs, payment screenshots and signed rules go here. Marketing photos live in Settings.</div>
        : groups.map(g => <div key={g.cat} style={{ marginBottom: 14 }}><div className="kicker-sm" style={{ marginBottom: 8 }}>{g.cat}</div><DocGrid docs={g.docs} /></div>)}
    </section>
  );
}

function CommsTab({ r }: { r: BookingRecord }) {
  const S = useStore(), sug = suggestTemplates(r, S.now), c = S.cust(r.cust);
  return (
    <div className="two-col">
      <section className="card">
        <div className="section-head"><h3 className="card-sub">Suggested now</h3><button className="btn small" onClick={() => S.open('message', { id: r.id })}>All templates</button></div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {sug.map((key, i) => <button key={key} className={`btn ${i === 0 ? 'primary' : ''}`} onClick={() => S.open('message', { id: r.id, tpl: key })}>{i === 0 && <Icon name="message" size={15} />}{S.state.settings.templates[key]?.label ?? key}</button>)}
        </div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>Messages open in WhatsApp or are copied for you to paste. The app records that you prepared them, not that they were sent.</div>
      </section>
      <section className="card">
        <h3 className="card-sub" style={{ marginBottom: 10 }}>History</h3>
        {r.comms.length === 0 ? <div className="muted" style={{ fontSize: 13 }}>No messages prepared yet.</div> : [...r.comms].reverse().map(x => (
          <button key={x.id} className="comm" onClick={() => S.open('message', { id: r.id, tpl: x.key })}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}><b>{S.state.settings.templates[x.key]?.label ?? x.key}</b><span className="muted num" style={{ fontSize: 12 }}>{fDT(x.date)} · {x.action}</span></div>
            <div className="muted ellipsis" style={{ fontSize: 12.5 }}>{renderMessage(x.key, r, c, S.state.settings).replace(/\n+/g, ' ')}</div>
          </button>
        ))}
      </section>
    </div>
  );
}

function ActivityTab({ r }: { r: BookingRecord }) {
  const S = useStore();
  return (
    <section className="card">
      <div className="timeline">
        {activityFor(S, r).map((a, i) => <div key={i} className="tl"><span className="tl-dot" /><div><div style={{ fontSize: 13.5 }}>{a.t}</div><div className="muted num" style={{ fontSize: 12 }}>{fDT(a.d)}{a.by ? ` · ${a.by}` : ''}</div></div></div>)}
      </div>
    </section>
  );
}
